<template>
  <div>
    <v-row>
      <v-col cols="12" sm="3" offset-sm="6" class="py-0">
        <v-text-field
          v-model="search"
          color="dw_green"
          label="Search"
          clearable
          :clear-icon="mdiClose"
          single-line
          hide-details
          class="text-caption">
          <template v-slot:append>
            <v-icon small class="ml-1" v-text="mdiLayersSearchOutline" />
          </template>
        </v-text-field>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="9">
        <v-data-table
          dense
          :headers="headers"
          :items="playOptions"
          :search="search"
          item-key="id"
          :single-select="true"
          @click:row="(rowItem) => selectPlay(rowItem)"
          class="my-2"
          :footer-props="{
            showFirstLastPage: true,
            firstIcon: mdiPageFirst,
            lastIcon: mdiPageLast,
            prevIcon: mdiChevronLeft,
            nextIcon: mdiChevronRight
          }"
          :options="{
            itemsPerPage: 5
          }"
        >
        </v-data-table>
          <v-card flat color="bg_dark" v-if="selectedPlay">
            <v-card-title>{{ selectedPlay.id }} - {{ selectedPlay.name }}</v-card-title>
            <v-card-subtitle class="font-weight-light">
              {{ selectedPlay.description }}
            </v-card-subtitle>
            <v-card-text class="pa-0">
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-subtitle>
                    CAPEC&trade; Attack Patterns
                  </v-list-item-subtitle>
                  <v-chip-group column>
                    <v-chip
                      v-if="selectedPlay.attackpatterns.length === 0"
                      label
                      outlined
                      small
                      color="grey">No Associated Attack Patterns</v-chip>
                    <v-chip
                      v-else
                      v-for="capec in selectedPlay.attackpatterns"
                      :key="`capec.${capec.id}`"
                      label
                      outlined
                      small
                      :color="capec.status === 'Deprecated' ? 'red' : 'dw_green'">
                      CAPEC-{{ capec.id }}
                    </v-chip>
                  </v-chip-group>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-subtitle>
                    MITRE ATT&amp;CK&reg; Techniques
                  </v-list-item-subtitle>
                  <v-chip-group column>
                    <v-chip
                      v-if="selectedPlay.techniques.length === 0"
                      label
                      outlined
                      small
                      color="grey">No Associated Techniques</v-chip>
                    <v-chip
                      v-else
                      v-for="teq in selectedPlay.techniques"
                      :key="`teq.${teq.id}`"
                      label
                      outlined
                      small
                      :color="(teq.revoked || teq.deprecated) ? 'red' : 'dw_green'">
                      {{ teq.mitre_id }}
                    </v-chip>
                  </v-chip-group>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-subtitle>
                    NIST SP 800-53 (Rev. 5) Controls
                  </v-list-item-subtitle>
                  <v-chip-group column>
                    <v-chip
                      v-if="selectedPlay.controls.length === 0"
                      label
                      outlined
                      small
                      color="grey">No Associated NIST SP 800-53 Controls</v-chip>
                    <v-chip
                      v-else
                      v-for="ctrl in selectedPlay.controls"
                      :key="`ctrl.${ctrl.sort_id}`"
                      label
                      outlined
                      small
                      color="dw_green">
                      {{ ctrl.label }}
                    </v-chip>
                  </v-chip-group>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-subtitle>
                    CWE&trade; Weaknesses
                  </v-list-item-subtitle>
                  <v-chip-group column>
                    <v-chip
                      v-if="selectedPlay.cwes.length === 0"
                      label
                      outlined
                      small
                      color="grey">No Associated Weaknesses</v-chip>
                    <v-chip
                      v-else
                      v-for="cwe in selectedPlay.cwes"
                      :key="`cwe.${cwe.id}`"
                      label
                      outlined
                      small
                      :color="cwe.status === 'Deprecated' ? 'red' : 'dw_green'">
                      CWE-{{ cwe.id }}
                    </v-chip>
                  </v-chip-group>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-subtitle>
                    Tools
                  </v-list-item-subtitle>
                  <ul class="font-weight-light">
                    <li v-if="selectedPlay.tools.length === 0">No Associated Tools</li>
                    <li v-else v-for="tool in selectedPlay.tools" :key="`tool.${tool.id}`">
                      <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
                      {{ tool.name }}
                    </li>
                  </ul>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-subtitle>
                    Tags
                  </v-list-item-subtitle>
                  <ul class="font-weight-light">
                    <li v-if="selectedPlay.tags.length === 0">No Associated Tags</li>
                    <li v-else v-for="tag in selectedPlay.tags" :key="`tag.${tag.id}`">
                      <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
                      {{ tag.name }}
                    </li>
                  </ul>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-subtitle>
                    Objectives
                  </v-list-item-subtitle>
                  <ul class="font-weight-light">
                    <li v-if="selectedPlay.objectives.length === 0">No Associated Objectives</li>
                    <li v-else v-for="objtv in selectedPlay.objectives" :key="`objtv.${objtv.id}`">
                      <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
                      {{ objtv.title }}
                    </li>
                  </ul>
                </v-list-item-content>
              </v-list-item>
            </v-card-text>
            <v-card-actions>
              <v-btn
                color="red"
                text
                @click="archive"
                title="Remove Play">
                Remove
                <v-icon v-text="mdiArchiveOutline" small class="ml-2" />
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import {
  mdiArchiveOutline,
  mdiClose,
  mdiLayersSearchOutline,
  mdiPageFirst,
  mdiPageLast,
  mdiChevronLeft,
  mdiChevronRight,
  mdiSquareMediumOutline
} from '@mdi/js';

export default {
  name: 'archivePlays',
  data() {
    return {
      mdiArchiveOutline,
      mdiClose,
      mdiLayersSearchOutline,
      mdiPageFirst,
      mdiPageLast,
      mdiChevronLeft,
      mdiChevronRight,
      mdiSquareMediumOutline,
      search: '',
      headers: [
        { text: 'ID', value: 'id', class: 'font-weight-regular' },
        { text: 'Name', value: 'name', class: 'font-weight-regular' }
      ],
      items: undefined,
      selectedPlay: undefined,
      formValid: false
    };
  },
  computed: {
    ...mapGetters([
      'playOptions'
    ])
  },
  methods: {
    ...mapActions([
      'archivePlay',
      'fetchPlayOptions',
      'fetchOriginalByID'

    ]),
    async selectPlay(play) {
      this.selectedPlay = await this.fetchOriginalByID(['play', play.id]);
    },
    async archive() {
      try {
        await this.archivePlay({
          id: this.selectedPlay.id,
          name: this.selectedPlay.name,
          archived: true
        });
        this.selectedPlay = undefined;
        await this.fetchPlayOptions();
      } catch (err) {
        console.error(err);
      }
    }
  }
};
</script>

<style scoped>
ul {
  padding: 0 0 0 0;
}
li {
  list-style-type: none;
}
</style>
